import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [strip] - The scrollable strip of extra media (grid + carousel presentation).
 * @property {HTMLButtonElement} [stripPreviousButton] - The strip's previous arrow.
 * @property {HTMLButtonElement} [stripNextButton] - The strip's next arrow.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.refs.strip) {
      this.refs.strip.addEventListener('scroll', this.#updateStripArrows, { signal });
      this.#stripResizeObserver.observe(this.refs.strip);
      this.#updateStripArrows();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#stripResizeObserver.disconnect();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Scrolls the strip carousel back by one page of items.
   */
  stripPrevious() {
    this.#scrollStripBy(-1);
  }

  /**
   * Scrolls the strip carousel forward by one page of items.
   */
  stripNext() {
    this.#scrollStripBy(1);
  }

  /**
   * Scrolls the strip carousel by one page of items.
   *
   * @param {number} direction - The direction to scroll: -1 for previous, 1 for next.
   */
  #scrollStripBy(direction) {
    const { strip } = this.refs;

    if (!strip) return;

    const gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    strip.scrollBy({ left: direction * (strip.clientWidth + gap), behavior });
  }

  #updateStripArrows = () => {
    const { strip, stripPreviousButton, stripNextButton } = this.refs;

    if (!strip) return;

    const maxScroll = strip.scrollWidth - strip.clientWidth;
    const scrolled = Math.abs(strip.scrollLeft);

    stripPreviousButton?.toggleAttribute('disabled', scrolled <= 1);
    stripNextButton?.toggleAttribute('disabled', scrolled >= maxScroll - 1);
  };

  #stripResizeObserver = new ResizeObserver(this.#updateStripArrows);

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
